import React, { useState, useEffect, useCallback } from 'react';
import { ShoppingCart, FileText, Loader2, Link2, X, AlertCircle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { contractsApi } from '../../lib/api';

/**
 * Modal brandizzato per convertire un preventivo accettato in vendita.
 * Sostituisce i window.confirm/prompt nativi con UX coerente al brand Main Solution.
 *
 * Props:
 *  - open: bool
 *  - onClose: fn
 *  - quote: { quote_number, customer, total_gross, ... }
 *  - onConfirm: async fn({ contract_id?, notes? }) — chiamata quando l'utente conferma
 */
export const ConvertToSaleDialog = ({ open, onClose, quote, onConfirm }) => {
  const [mode, setMode] = useState('existing'); // 'existing' | 'manual' | 'none'
  const [contracts, setContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState('');
  const [manualContractId, setManualContractId] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadContracts = useCallback(async () => {
    setLoadingContracts(true);
    try {
      // Filtra per cognome cliente se disponibile, per facilitare la ricerca
      const params = { limit: 50 };
      const cognome = quote?.customer?.cognome;
      if (cognome) params.search = cognome;
      const res = await contractsApi.getAll(params);
      const items = res.data?.items || res.data || [];
      setContracts(Array.isArray(items) ? items : []);
    } catch (err) {
      console.warn('Impossibile caricare contratti:', err);
      setContracts([]);
    } finally {
      setLoadingContracts(false);
    }
  }, [quote?.customer?.cognome]);

  // Reset quando si apre/chiude
  useEffect(() => {
    if (open) {
      setMode('existing');
      setSelectedContractId('');
      setManualContractId('');
      setNotes('');
      loadContracts();
    }
  }, [open, loadContracts]);

  const handleConfirm = async () => {
    let contractId = null;
    if (mode === 'existing') contractId = selectedContractId || null;
    else if (mode === 'manual') contractId = manualContractId.trim() || null;

    setSubmitting(true);
    try {
      await onConfirm({
        contract_id: contractId,
        notes: notes.trim() || null,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (v) => `€ ${(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}`;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent
        className="max-w-lg p-0 overflow-hidden"
        data-testid="convert-to-sale-dialog"
      >
        {/* Header brandizzato */}
        <div className="bg-gradient-to-br from-[#0B2A4A] to-[#00BCE7] text-white px-6 py-5">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Converti preventivo in vendita
            </DialogTitle>
          </DialogHeader>
          {quote && (
            <div className="mt-2 text-sm text-white/90 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="font-mono font-semibold">{quote.quote_number}</span>
              <span className="opacity-70">•</span>
              <span>{quote.customer?.nome} {quote.customer?.cognome}</span>
              <span className="opacity-70">•</span>
              <span className="font-bold text-amber-300">{formatPrice(quote.total_gross)}</span>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Selettore tipo collegamento */}
          <div>
            <Label className="text-xs uppercase tracking-wide text-[#0B2A4A] font-semibold">
              Collega contratto
              <span className="text-slate-400 font-normal ml-1">(opzionale)</span>
            </Label>
            <div className="flex gap-2 mt-2" data-testid="convert-mode-tabs">
              {[
                { id: 'existing', label: 'Contratto esistente', icon: Link2 },
                { id: 'manual', label: 'ID manuale', icon: FileText },
                { id: 'none', label: 'Nessuno', icon: X },
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setMode(id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                    mode === id
                      ? 'bg-[#00BCE7] text-white border-[#00BCE7] shadow-sm'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`convert-mode-${id}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Contenuto modale */}
          {mode === 'existing' && (
            <div>
              {loadingContracts ? (
                <div className="flex items-center gap-2 text-sm text-slate-500 py-3">
                  <Loader2 className="h-4 w-4 animate-spin" /> Caricamento contratti…
                </div>
              ) : contracts.length === 0 ? (
                <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="no-contracts-msg">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    Nessun contratto trovato per <strong>{quote?.customer?.cognome || 'questo cliente'}</strong>.
                    Passa a "ID manuale" o lascia "Nessuno".
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1" data-testid="contracts-list">
                  {contracts.map((c) => (
                    <label
                      key={c.id || c._id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedContractId === (c.id || c._id)
                          ? 'border-[#00BCE7] bg-cyan-50/60 ring-1 ring-cyan-200'
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                      data-testid={`contract-option-${c.id || c._id}`}
                    >
                      <input
                        type="radio"
                        className="accent-[#00BCE7]"
                        checked={selectedContractId === (c.id || c._id)}
                        onChange={() => setSelectedContractId(c.id || c._id)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-bold text-[#0B2A4A]">
                            {c.codice_commessa || c.id || c._id}
                          </span>
                          {c.product_code && (
                            <Badge variant="outline" className="text-[10px]">{c.product_code}</Badge>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 truncate">
                          {c.customer_name || `${c.customer?.nome || ''} ${c.customer?.cognome || ''}`.trim() || '—'}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {mode === 'manual' && (
            <div>
              <Input
                placeholder="Es: CNT-2026-0123 / VND-2026-0042"
                value={manualContractId}
                onChange={(e) => setManualContractId(e.target.value)}
                className="font-mono"
                data-testid="manual-contract-id-input"
              />
              <p className="text-xs text-slate-500 mt-1.5">
                Inserisci l'ID del contratto o della vendita se non è ancora presente nel CRM.
              </p>
            </div>
          )}

          {mode === 'none' && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-600" data-testid="no-link-msg">
              Conversione registrata senza collegamento a contratti. Potrai collegare il contratto in seguito.
            </div>
          )}

          {/* Note conversione */}
          <div>
            <Label className="text-xs uppercase tracking-wide text-[#0B2A4A] font-semibold">
              Note di conversione
              <span className="text-slate-400 font-normal ml-1">(opzionale)</span>
            </Label>
            <Textarea
              placeholder="Es: firma fissata 30/06, anticipo già versato…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="mt-2 resize-none"
              data-testid="conversion-notes-textarea"
            />
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex gap-2">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              <strong>Attenzione:</strong> una volta convertito, lo stato diventa "Convertito"
              e il preventivo <strong>non sarà più modificabile</strong>.
            </div>
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="px-6 py-4 bg-slate-50 border-t border-slate-200 gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            data-testid="cancel-convert-btn"
          >
            Annulla
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting || (mode === 'manual' && !manualContractId.trim())}
            className="bg-purple-600 hover:bg-purple-700 text-white min-w-[200px]"
            data-testid="confirm-convert-btn"
          >
            {submitting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Conversione in corso…</>
            ) : (
              <><ShoppingCart className="h-4 w-4 mr-2" /> Conferma conversione</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
